import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Button } from '@src/design-system/button';
import { cn } from '@src/design-system/helpers';

export type TableActionIconButtonProps = ComponentProps<typeof Button> & {
  label: string;
  icon: ReactNode;
  /** When false, icon-only — no expanding hover label. Default true. */
  showLabelOnHover?: boolean;
};

export const TableActionIconButton = forwardRef<
  HTMLButtonElement,
  TableActionIconButtonProps
>(function TableActionIconButton(
  {
    label,
    icon,
    className,
    size = 'iconSm',
    variant = 'primaryOutline',
    showLabelOnHover = true,
    ...props
  },
  ref
) {
  return (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      className={cn(
        // Grow on hover for the label; keep the idle footprint like iconSm.
        // Kill primaryOutline's hover:opacity-80 so color stays solid.
        'group h-10! w-auto! min-w-10 gap-0 px-2.5 text-gray-800 hover:opacity-100',
        className
      )}
      aria-label={label}
      {...props}
    >
      <span className='inline-flex shrink-0 items-center justify-center'>
        {icon}
      </span>
      {showLabelOnHover ? (
        <span className='font-medium text-gray-800 whitespace-nowrap translate-x-1 max-w-0 overflow-hidden transition-[max-width,margin,transform] duration-300 group-hover:ml-1 group-hover:translate-x-0 group-hover:max-w-[7.5rem]'>
          {label}
        </span>
      ) : null}
    </Button>
  );
});
